import React, { useEffect, useState } from "react";
import "./style.css";
import { useUsersContext } from "../../../../context/usersContext";
import { useUserInfo } from "../../../../context/user";
import {
  usePaymentInfo,
  useInvoices,
  useSubscriptionById,
  useAITokenPurchases,
  useAccountById,
  useAccountBalance,
  useAccountBalanceLogs,
  useLocationDataByChat,
} from "../../../../queries/billing";
import { useUpdateAccount } from "../../../../queries/settingsAccount";
import { CreditCard, Download, Eye, Pencil, Plus, Receipt } from "lucide-react";
import { Button } from "components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import InvoiceModal from "./component/InvoiceModal";
import EditPaymentMethod from "./component/EditPaymentMethodModal";
import EditBillingInfoModal from "./component/EditBillingInfoModal";
import BuyTokensModal from "./component/BuyTokensModal";
import AITasksCard from "./component/AITasksCard";
import format from "date-fns/format";
import CancelModal from "./component/CancelModal";
import CancelConfirmModal from "./component/CancelConfirmModal";
import ContactToSupportModal from "../../../../components/ContactToSupportModal";

const IconButton = ({ label, onClick, children }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className="interactive-chip inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const StatusPill = ({ status }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-wider font-medium border w-fit capitalize ${
      status === "paid"
        ? "bg-muted text-foreground border-border"
        : "bg-muted/40 text-muted-foreground border-dashed border-border"
    }`}
  >
    {status}
  </span>
);

function TeamContent({ setActiveTab }) {
  const updateAccount = useUpdateAccount();
  const user = useUserInfo()[0];
  const userActiveLocation = useUserInfo()[4];
  const Update = useUserInfo()[5];
  const [autoChargeAmount, setAutoChargeAmount] = useState(0);
  const [lowBalance, setLowBalance] = useState(0);
  const { getLocationUsers, products, agency } = useUsersContext();
  const [addAddUerModalOpen, setAddUserModalOpen] = useState(false);
  const [isOpenInvoiceModal, setIsOpenInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState(false);
  const [openBillingInfoModal, setOpenBillingInfoModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openBuyTokensModal, setOpenBuyTokensModal] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    lastName: "",
    email: "",
    contactNumber: "",
  });
  const [paymentMethodTitle, setPaymentMethodTitle] = useState("");
  const activePlan = products?.filter((product) => product?.activePlan)[0];
  const [openSupportModal, setOpenSupportModal] = useState(false);

  const handlePaymentMethodModal = (isEdit = false) => {
    setPaymentMethodTitle(
      isEdit ? "Edit Payment Method" : "Add Payment Method",
    );
    setOpenPaymentMethodModal(!openPaymentMethodModal);
  };

  const handleBillingInfoModal = () => {
    setOpenBillingInfoModal(!openBillingInfoModal);
  };

  const locationId = userActiveLocation?.id;
  const agencyId = userActiveLocation?.agency_id;
  const customerId = userActiveLocation?.customer_id;

  const { data: paymentInformation } = usePaymentInfo(locationId, agencyId, customerId);
  const { data: invoicesData = [] } = useInvoices(locationId, agencyId, customerId);
  const { data: subscription } = useSubscriptionById(
    locationId,
    userActiveLocation?.subscription_id,
    agencyId,
  );
  const { data: aiTokenPurchases = [] } = useAITokenPurchases(locationId, userActiveLocation?._id);

  // Was a manual setInterval(refreshAccount, 15000) — now the query's own
  // refetchInterval, still pushed into context via Update() on every fetch.
  const { data: accountPollData } = useAccountById(locationId, { refetchInterval: 15000 });
  useEffect(() => {
    if (accountPollData) Update(accountPollData);
  }, [accountPollData]);

  const justCloseIt = () => {
    setAddUserModalOpen(false);
  };

  const handleAddUserModal = (e) => {
    e.preventDefault();
    setAddUserModalOpen(true);
  };
  const handleAutoCharge = async (e) => {
    const payload = {
      auto_charge: e.target.value,
      _id: userActiveLocation?.id,
    };
    try {
      const response = await updateAccount.mutateAsync(payload);
      if (response) {
        setAutoChargeAmount(response?.data?.auto_charge);
        Update(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const handleLowBalance = async (e) => {
    const payload = {
      low_balancs_limit: e.target.value,
      _id: userActiveLocation?.id,
    };
    try {
      const response = await updateAccount.mutateAsync(payload);
      if (response) {
        setLowBalance(response?.data?.low_balancs_limit);
        Update(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { data: balanceData } = useAccountBalance(locationId, locationId);
  useEffect(() => {
    if (balanceData) {
      setAutoChargeAmount(balanceData.auto_chart ?? 0);
      setLowBalance(balanceData.low_balancs_limit ?? 0);
    }
  }, [balanceData]);
  const accountbalance = balanceData?.amount ?? 0;

  const { data: balanceLogsData } = useAccountBalanceLogs(locationId, agencyId);
  const accountbalancelogs = balanceLogsData?.data ?? [];

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };
  const getLogMessage = (log) => {
    if (log.type === "credit") {
      return `Received new amount of <b style="color:green">$${log.amount
        }</b> on <b>${log.req_type}   <br>${formatDate(log.createdAt)}  ${log.response.status
        }</b>`;
    } else if (log.type === "debit") {
      return `Paid amount of <b style="color:red">$${log.amount}</b> on <b>${log.req_type
        }  <br>${formatDate(log.createdAt)} ${log.response.status}</b>`;
    } else {
      return `Unknown transaction type`;
    }
  };
  const location = useUserInfo();
  const agencyData = useUserInfo()[11];
  const { data: locationDataByChat = [] } = useLocationDataByChat(location?._id);
  const totalMatchLocationfromchat = locationDataByChat.length;

  const formatAmount = (amount, currency) => {
    const sym = (currency || "usd").toUpperCase() === "GBP" ? "£" : "$";
    return `${sym}${Number(amount || 0).toFixed(2)}`;
  };

  const paymentRows = [
    ...(invoicesData || [])?.map((item) => ({
      id: `inv_${item.id}`,
      type: "invoice",
      date: item.created ? new Date(item.created * 1000) : new Date(0),
      description:
        item.description || item.lines?.data?.[0]?.description || "Invoice",
      amount: formatAmount((item.amount_paid || 0) / 100, item.currency),
      status: item.status,
      raw: item,
    })),
    ...(aiTokenPurchases || [])
      .filter((p) => p?.status === "succeeded")
      .map((p) => ({
        id: `ai_${p._id}`,
        type: "ai_tokens",
        date: p.createdAt ? new Date(p.createdAt) : new Date(0),
        description: p.description || `${p.tokens} Bulk AI Task`,
        amount: formatAmount(p.amount, p.currency),
        status: "paid",
        raw: p,
      })),
  ].sort((a, b) => b.date - a.date);

  const pdfRows = paymentRows.filter(
    (row) => row.type === "invoice" && row.raw?.invoice_pdf,
  );

  const handleDownloadAll = () => {
    pdfRows.forEach((row, i) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = row.raw.invoice_pdf;
        a.download = `invoice_${row.raw.id || "000000"}.pdf`;
        a.target = "_blank";
        a.rel = "noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, i * 300);
    });
  };

  const handleChangePlanClick = () => {
    if (userActiveLocation?.type !== 'free' && (activePlan?.isenable_account || activePlan?.developer_plan)) {
      setActiveTab("Pricing");
    } else {
      setOpenSupportModal(true);
    }
  };

  return (
    <>
      <div>
        <div className="page-header [&_.page-display]:text-3xl [&_.page-display]:md:text-4xl [&_.page-lede]:text-sm pb-4">
          <span className="page-eyebrow">Configure</span>
          <h1 className="page-display">
            Billing.{" "}
            <span className="accent block md:inline">
              Your plan, payment, and invoices.
            </span>
          </h1>
          <p className="page-lede">
            Manage your subscription, update payment details, and download past
            invoices.
          </p>
        </div>

        {/* Plan summary */}
        <div className="rounded-xl border border-border bg-card p-6 mb-5 max-w-4xl">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-foreground">
                  {activePlan?.plan?.planName}
                </h2>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-muted text-[11px] font-medium text-foreground border border-border">
                  Active
                </span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {`Your current billing cycle ends on ${
                  subscription
                    ? format(
                        new Date(subscription?.current_period_end * 1000),
                        "MMMM dd, yyyy",
                      )
                    : ""
                }.`}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                onClick={handleChangePlanClick}
                className="h-9 px-4 text-xs font-semibold rounded-md bg-foreground text-background hover:bg-foreground/90"
              >
                Change Plan
              </Button>
              {!activePlan?.is_free_saas && (
                <Button
                  variant="outline"
                  onClick={() => setOpenCancelModal(true)}
                  className="h-9 px-4 text-xs font-semibold rounded-md"
                >
                  Cancel Plan
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Payment + billing info cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5 max-w-4xl">
          <div className="interactive-surface rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Payment Method
              </span>
              {paymentInformation?.card ? (
                <IconButton
                  label="Edit payment method"
                  onClick={() => handlePaymentMethodModal(true)}
                >
                  <Pencil className="h-4 w-4" />
                </IconButton>
              ) : (
                <IconButton
                  label="Add payment method"
                  onClick={() => handlePaymentMethodModal(false)}
                >
                  <Plus className="h-4 w-4" />
                </IconButton>
              )}
            </div>
            {paymentInformation?.card ? (
              <div className="mt-4 flex items-center gap-3">
                <div className="h-10 w-14 rounded-md border border-border bg-background flex items-center justify-center shrink-0">
                  <CreditCard className="h-5 w-5 text-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground capitalize">
                    {paymentInformation?.card?.brand} ····{" "}
                    {paymentInformation?.card?.last4}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Expires {paymentInformation?.card?.exp_month}/
                    {paymentInformation?.card?.exp_year}
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                No payment method added
              </p>
            )}
          </div>

          {paymentInformation && (
            <div className="interactive-surface rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Billing Information
                </span>
                <IconButton
                  label="Edit billing information"
                  onClick={() => handleBillingInfoModal(true)}
                >
                  <Pencil className="h-4 w-4" />
                </IconButton>
              </div>
              <div className="mt-4 text-sm text-foreground space-y-0.5">
                <p className="font-medium">
                  {paymentInformation?.billing_details?.name}
                </p>
                {paymentInformation?.billing_details?.address?.line1 && (
                  <p className="text-muted-foreground">
                    {paymentInformation.billing_details.address.line1}
                  </p>
                )}
                {paymentInformation?.billing_details?.address?.line2 && (
                  <p className="text-muted-foreground">
                    {paymentInformation.billing_details.address.line2}
                  </p>
                )}
                {(paymentInformation?.billing_details?.address?.city ||
                  paymentInformation?.billing_details?.address?.state) && (
                  <p className="text-muted-foreground">
                    {[
                      paymentInformation?.billing_details?.address?.city,
                      paymentInformation?.billing_details?.address?.state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
                {paymentInformation?.billing_details?.address?.postal_code && (
                  <p className="text-muted-foreground">
                    {paymentInformation.billing_details.address.postal_code}
                  </p>
                )}
                {paymentInformation?.billing_details?.address?.country && (
                  <p className="text-muted-foreground">
                    {paymentInformation.billing_details.address.country}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="max-w-4xl mb-10">
          <AITasksCard
            userActiveLocation={userActiveLocation}
            subscription={subscription}
            onBuyClick={() => setOpenBuyTokensModal(true)}
          />
        </div>

        <div className="max-w-5xl">
          <div className="flex items-end justify-between gap-3 mb-3">
            <h2 className="text-base font-semibold text-foreground">
              Payment History
            </h2>
            <Button
              variant="outline"
              disabled={pdfRows.length === 0}
              onClick={handleDownloadAll}
              className="h-9 gap-2 text-sm rounded-md"
            >
              <Download className="h-4 w-4" /> Download all
            </Button>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="hidden md:grid grid-cols-[160px_minmax(0,2fr)_120px_100px_120px] items-center gap-4 px-4 py-3 border-b border-border bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
              <span>Due Date</span>
              <span>Description</span>
              <span className="text-right">Amount</span>
              <span>Status</span>
              <span className="sr-only">Actions</span>
            </div>
            {paymentRows.length ? (
              <ul>
                {paymentRows.map((row) => {
                  const rowDate = format(row.date, "MMMM dd, yyyy");
                  const actions = (
                    <div className="flex items-center justify-end gap-1">
                      {row.type === "invoice" && (
                        <IconButton
                          label={`View invoice ${rowDate}`}
                          onClick={() => {
                            setIsOpenInvoiceModal(true);
                            setSelectedInvoice(row.raw);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </IconButton>
                      )}
                      {row.type === "invoice" && row.raw?.invoice_pdf && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={row.raw.invoice_pdf}
                                target="_blank"
                                download={`invoice_${row.raw.id || "000000"}.pdf`}
                                rel="noreferrer"
                                aria-label={`Download invoice ${rowDate}`}
                                className="interactive-chip inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>Download PDF</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {row.type === "ai_tokens" && row.raw?.receipt_url && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={row.raw.receipt_url}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`View receipt ${rowDate}`}
                                className="interactive-chip inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                              >
                                <Receipt className="h-4 w-4" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>View receipt</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {row.type !== "invoice" && !row.raw?.receipt_url && (
                        <span className="text-muted-foreground pr-2">—</span>
                      )}
                    </div>
                  );
                  return (
                    <li
                      key={row.id}
                      className="interactive-surface border-b border-border last:border-b-0"
                    >
                      {/* Mobile: stacked card */}
                      <div className="md:hidden px-4 py-3 flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                              {rowDate}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {row.description}
                            </p>
                          </div>
                          <p className="text-sm text-foreground tabular-nums shrink-0">
                            {row.amount}
                          </p>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <StatusPill status={row.status} />
                          {actions}
                        </div>
                      </div>

                      {/* Desktop: grid row */}
                      <div className="hidden md:grid grid-cols-[160px_minmax(0,2fr)_120px_100px_120px] items-center gap-4 px-4 py-3">
                        <span className="text-sm text-muted-foreground">
                          {rowDate}
                        </span>
                        <span className="text-sm text-foreground truncate">
                          {row.description}
                        </span>
                        <span className="text-sm text-foreground text-right tabular-nums">
                          {row.amount}
                        </span>
                        <span>
                          <StatusPill status={row.status} />
                        </span>
                        {actions}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="empty-state">
                <p className="empty-title">No payment history</p>
                <p className="empty-lede">
                  Your invoices will appear here after your next billing cycle.
                </p>
              </div>
            )}
          </div>
        </div>
        <InvoiceModal
          setIsOpenInvoiceModal={setIsOpenInvoiceModal}
          isOpenInvoiceModal={isOpenInvoiceModal}
          invoices={selectedInvoice}
        />
        <EditPaymentMethod
          isOpen={openPaymentMethodModal}
          onClose={handlePaymentMethodModal}
          title={paymentMethodTitle}
          agency={agency}
          userActiveLocation={userActiveLocation}
        />
        {openBillingInfoModal && (
          <EditBillingInfoModal
            isOpen={openBillingInfoModal}
            onClose={handleBillingInfoModal}
            paymentMethod_id={paymentInformation?.id}
            billing_details={paymentInformation?.billing_details}
            userActiveLocation={userActiveLocation}
          />
        )}
        {openCancelModal && (
          <CancelModal
            isOpen={openCancelModal}
            onClose={() => {
              setOpenCancelModal(false);
            }}
            setOpenConfirmModal={setOpenConfirmModal}
          />
        )}
        {openConfirmModal && (
          <CancelConfirmModal
            isOpen={openConfirmModal}
            onClose={() => setOpenConfirmModal(false)}
          />
        )}
        <BuyTokensModal
          isOpen={openBuyTokensModal}
          onClose={() => setOpenBuyTokensModal(false)}
          userActiveLocation={userActiveLocation}
          onPurchased={(updatedAccount) => {
            if (updatedAccount) Update(updatedAccount);
          }}
        />
      </div>
      {openSupportModal && (
        <ContactToSupportModal
          isOpen={openSupportModal}
          onClose={() => setOpenSupportModal(false)}
        />
      )}
    </>
  );
}

export default TeamContent;
